import { LenderFieldMap } from '@trnsact/trnsact-shared-types';
import { Engine } from 'json-rules-engine';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { accountSelectors } from 'redux/accountReducer';

export function useDynamicLenderSubmissionDlmEngine(
  dynamicLenderMappings: LenderFieldMap[],
  handleDlmRulesEvents: (events: any) => void
) {
  const accountId = useSelector(accountSelectors.accountId);
  const [dlmRulesEngine] = useState(() => new Engine());
  const [isReady, setIsReady] = useState(false);
  const handleDlmRulesEventsRef = useRef(handleDlmRulesEvents);
  handleDlmRulesEventsRef.current = handleDlmRulesEvents;

  useEffect(() => {
    if (!dynamicLenderMappings.length) return;

    dlmRulesEngine.addOperator('doesNotContainRegex', (factValue, regexValue) => {
      try {
        if (typeof factValue !== 'string') {
          return true;
        }
        if (typeof regexValue !== 'string') {
          return true;
        }
        const regex = new RegExp(regexValue);
        return !regex.test(factValue);
      } catch (e) {
        console.error(`Error in doesNotContainRegex operator: ${e instanceof Error ? e.message : String(e)}`);
        return true;
      }
    });

    dlmRulesEngine.addOperator('containsRegex', (factValue, regexValue) => {
      try {
        if (typeof factValue !== 'string' || typeof regexValue !== 'string') {
          return false;
        }
        const regex = new RegExp(regexValue);
        return regex.test(factValue);
      } catch (e) {
        console.error(`Error in containsRegex operator: ${e instanceof Error ? e.message : String(e)}`);
        return true;
      }
    });

    dlmRulesEngine.addFact('accountId', accountId);

    try {
      dynamicLenderMappings.forEach(field => {
        if (!field.lenderProperty || !field.details) {
          return;
        }

        dlmRulesEngine.addFact(field.lenderProperty, field.mappedValue);
        dlmRulesEngine.addFact(`${field.lenderProperty}_originalMappedValue`, field.originalMappedValue);
        dlmRulesEngine.addFact(`${field.lenderProperty}_hidden`, field.details?.hidden ?? false);

        if (
          field.details.fieldInputType === 'OPTION_SET' &&
          field.details.optionSets === null &&
          field.details.lenderFieldParentLenderProperty
        ) {
          dlmRulesEngine.addRule({
            conditions: {
              any: [
                {
                  fact: field.details.lenderFieldParentLenderProperty,
                  operator: 'notEqual',
                  value: undefined,
                },
              ],
            },
            event: {
              type: `${field.lenderProperty}CascadingOptionSets`,
              params: {
                actions: [
                  {
                    type: 'getCascadingOptionSets',
                    inputKey: field.lenderProperty,
                    lenderFieldId: field.lenderFieldId,
                    parentInputKey: field.details.lenderFieldParentLenderProperty,
                    parentLenderFieldId: field.details.lenderFieldParentId,
                  },
                ],
              },
            },
          });
        }

        if (field.details.optionSets) {
          dlmRulesEngine.addFact(`${field.lenderProperty}-optionSets`, field.details.optionSets);
        }

        const rules: any[] = field.details.rulesConfiguration?.rules;
        if (rules) {
          rules.forEach(rule => {
            const conditionsAll = rule.conditions.all ? rule.conditions.all : [];
            const conditionsAny = rule.conditions.any ? rule.conditions.any : [];
            const conditionsNot = rule.conditions.not ? rule.conditions.not : [];
            const conditions: any[] = conditionsAll.concat(conditionsAny).concat(conditionsNot);
            conditions.forEach(c => {
              if (c.fact && !dlmRulesEngine.getFact(c.fact)) {
                dlmRulesEngine.addFact(c.fact, null);
              }
              if (c?.value?.fact && !dlmRulesEngine.getFact(c.value.fact)) {
                dlmRulesEngine.addFact(c.value.fact, null);
              }
            });
            dlmRulesEngine.addRule(rule);
          });
        }
      });
    } catch (e) {
      console.error(`Error adding DLM Rule: ${e instanceof Error ? e.message : String(e)}`);
    }

    dlmRulesEngine
      .run()
      .then(({ events }) => {
        handleDlmRulesEventsRef.current(events);
        setIsReady(true);
      })
      .catch(e => {
        console.error(`Error running DLM Rules Engine: ${e instanceof Error ? e.message : String(e)}`);
      });
  }, []);

  const runRulesEngine = (lenderProperty: string, newValue: any) => {
    dlmRulesEngine.addFact(lenderProperty, newValue);
    dlmRulesEngine.run().then(({ events }) => {
      handleDlmRulesEventsRef.current(events);
    });
  };

  return {
    dlmRulesEngine,
    isReady,
    runRulesEngine,
  };
}
